import React, { type ReactElement } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import Loading from 'atom-components/loading';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import {
  markersOfResponseParams,
  organisationUsersWithMarkerRoleParams
} from 'pages/marking/helpers/request-params.helpers';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import type { TestInstanceResponseMarkWithActiveMarkerAndResponse } from 'services/assist-api/entities/test-instance-response-mark/types';
import { useTestInstanceResponseMarkers } from 'services/assist-api/entities/test-instance-response-marker/hooks';
import { type TestInstanceResponseMarkerWithUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { isInitialLoad } from 'utils/check-query-status';
import ChangeActiveMarkerForm from './change-active-marker-form';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  mark: TestInstanceResponseMarkWithActiveMarkerAndResponse;
};

export default function ChangeActiveMarkerModal({ isOpen, onClose, mark }: Props): ReactElement {
  // Get the current user's organisation from the cache. We should have an earlier check to ensure
  // that the user has joined and is switched to an organisation by this point:
  const currentOrganisation = useAuthCustomer();
  const currentOrganisationId = currentOrganisation?.id;

  // Get existing markers of the selected response:
  const testInstanceResponseMarkersQuery =
    useTestInstanceResponseMarkers<TestInstanceResponseMarkerWithUser>(
      markersOfResponseParams(mark.testInstanceResponse.id)
    );

  // Get all possible markers:
  const organisationUsersQuery = useCustomerUsers(
    organisationUsersWithMarkerRoleParams(currentOrganisationId)
  );

  const isLoading =
    !currentOrganisation || isInitialLoad(testInstanceResponseMarkersQuery, organisationUsersQuery);

  const isError = testInstanceResponseMarkersQuery.isError || organisationUsersQuery.isError;

  const markers = testInstanceResponseMarkersQuery.data?.json || [];
  const organisationUsers = organisationUsersQuery.data?.json || [];

  return (
    <Modal isOpen={isOpen} size="lg">
      <ModalHeader toggle={onClose}>Change Active Marker</ModalHeader>
      <ModalBody>
        <>
          {isLoading && <Loading overlay={false} />}
          {!isLoading && isError && (
            <LoadingErrorAlert
              queries={[testInstanceResponseMarkersQuery, organisationUsersQuery]}
            />
          )}
          {!isLoading && !isError && (
            <ChangeActiveMarkerForm
              mark={mark}
              markers={markers}
              organisationUsers={organisationUsers}
              onClose={onClose}
            />
          )}
        </>
      </ModalBody>
    </Modal>
  );
}
